/**
 * WorksheetBatchPanel — page10-B detail-oldali csoportnézet (#435 seam).
 *
 * Egy beadás (közös `intakeBatchId`, al-szám ML-…-A/B/C) munkalapjait listázza
 * az overview-tabon. A mockup multi-equipment accordionjának option-B megfelelője
 * (#422: „a lista/detail csoport-nézetben mutatja — a mockup accordionja = a
 * batch worksheetjei"). A lekérés a meglévő lista-endpoint `?intakeBatchId=`
 * szűrője; az `intakeBatchId` a wire-en jön (mapper #435), a pinnelt read-schema
 * append-je Zeno-lane (addig FE-oldali opcionális mező).
 */

import { WorksheetStatus } from '@/api/worksheets';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBatchWorksheets } from '@/hooks/use-batch-worksheets';
import { useTranslation } from '@kgc/i18n';
import { useNavigate } from 'react-router-dom';
import { STATUS_LABEL_KEYS } from '../types';

export function WorksheetBatchPanel({
  currentWorksheetId,
  intakeBatchId,
}: {
  currentWorksheetId: string;
  intakeBatchId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { batchWorksheets, error } = useBatchWorksheets(intakeBatchId);

  if (!intakeBatchId) return null;

  if (error) {
    return (
      <p data-testid="worksheet-batch-error" className="text-sm text-muted-foreground">
        {t('worksheet.intakeBatch.error')}
      </p>
    );
  }

  if (batchWorksheets.length === 0) return null;

  const sorted = [...batchWorksheets].sort((a, b) =>
    a.worksheetNumber.localeCompare(b.worksheetNumber, 'hu')
  );

  const statusLabel = (status: string): string => {
    const key = STATUS_LABEL_KEYS[status as WorksheetStatus] as string | undefined;
    return key ? t(key) : status;
  };

  return (
    <Card data-testid="worksheet-batch-panel">
      <CardHeader>
        <CardTitle>{t('worksheet.intakeBatch.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          {t('worksheet.intakeBatch.count', { count: sorted.length })}
        </p>
        <ol className="space-y-2">
          {sorted.map(ws => {
            const isCurrent = ws.id === currentWorksheetId;
            const goTo = () => navigate(`/worksheet/${ws.id}`);
            return (
              <li
                key={ws.id}
                data-testid="worksheet-batch-item"
                data-current={isCurrent ? 'true' : undefined}
                {...(isCurrent
                  ? {}
                  : {
                      role: 'button',
                      tabIndex: 0,
                      'aria-label': ws.worksheetNumber,
                      onClick: goTo,
                      onKeyDown: (e: React.KeyboardEvent<HTMLLIElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goTo();
                        }
                      },
                    })}
                className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                  isCurrent
                    ? 'border-primary/40 bg-primary/5'
                    : 'cursor-pointer border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                }`}
              >
                <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                  {ws.worksheetNumber}
                </span>
                <span className="text-muted-foreground">{ws.deviceName}</span>
                <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-muted-foreground dark:bg-gray-800">
                  {statusLabel(ws.status)}
                </span>
                {isCurrent && (
                  <span className="text-[11px] italic text-muted-foreground">
                    {t('worksheet.intakeBatch.current')}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
